<script lang="ts" setup>
import type { DisplayableOfferProperty, OfferSmall, OfferSmallImage } from '~/types/Catalog';

const { selectedOffer } = storeToRefs(useCatalogStore());

function getMainImage(offer: OfferSmall): OfferSmallImage {
  return offer.offer_images.find((image) => image.is_main) ?? offer.offer_images[0];
}

function getOfferColor(offer: OfferSmall): string {
  return offer.displayable_offer_properties.find((p) => p.name === 'Цвет')?.value ?? '';
}

function filterAndSortOffersByDisplayProperty(displayProperty: DisplayableOfferProperty) {
  if (selectedOffer.value.displayable_offer_properties.length === 1) {
    return sortOrdersBySizeIfSizeExists(selectedOffer.value.offers, displayProperty);
  }

  let offers: OfferSmall[] = [];

  if (displayProperty.name === 'Цвет') {
    const uniqueColors: { color: string; sku: string }[] = [];

    for (const offer of selectedOffer.value.offers) {
      const colorValue = getOfferColor(offer);

      if (uniqueColors.findIndex((c) => c.color === colorValue) === -1) {
        offers.push(offer);
        uniqueColors.push({ color: colorValue, sku: offer.slug });
      }
    }
  } else {
    const selectedColor = selectedOffer.value.displayable_offer_properties.find((p) => p.name === 'Цвет')?.value;

    offers = selectedOffer.value.offers.filter((offer) => getOfferColor(offer) === selectedColor);
    offers = sortOrdersBySizeIfSizeExists(offers, displayProperty);
  }

  return offers;
}

function sortOrdersBySizeIfSizeExists(offers: OfferSmall[], displayProperty: DisplayableOfferProperty): OfferSmall[] {
  if (
    ['Размер основной', 'Размер'].includes(displayProperty.name) &&
    offers.every((offer) => offer.size_charts_values?.length)
  ) {
    offers.sort((a, b) => (a.size_charts_values[0].order < b.size_charts_values[0].order ? -1 : 1));
  }

  return offers;
}

function checkIsOffersPropertySelected(offer: OfferSmall, displayProperty: DisplayableOfferProperty) {
  const selectedOfferProp = selectedOffer.value.displayable_offer_properties.find(
    (p) => p.name === displayProperty.name,
  );
  const currentOfferProp = offer.displayable_offer_properties.find((p) => p.name === displayProperty.name);

  return selectedOfferProp?.value === currentOfferProp?.value;
}
</script>

<template>
  <section
    v-if="selectedOffer && selectedOffer.displayable_offer_properties.length"
    class="product-page__offers"
  >
    <div
      v-for="displayProperty in selectedOffer.displayable_offer_properties"
      :key="displayProperty.id"
      class="product-page__offers-item"
    >
      <p>
        {{ displayProperty.name }}: <span class="font-bold">{{ displayProperty.value }}</span>
      </p>

      <div class="flex gap-2 flex-wrap">
        <AppLink
          v-for="offer in filterAndSortOffersByDisplayProperty(displayProperty)"
          :key="offer.id"
          :class="{ 'product-page__offers-btn--selected': checkIsOffersPropertySelected(offer, displayProperty) }"
          :to="offer.uri"
          class="product-page__offers-btn"
        >
          <picture
            v-if="displayProperty.name === 'Цвет' && offer.offer_images.length"
            class="w-16 h-16 p-1"
          >
            <source
              :srcset="getMainImage(offer).conversions_webp.thumb_url_180"
              type="image/webp"
            />
            <source
              :srcset="getMainImage(offer).conversions.thumb_url_65"
              type="image/jpeg"
            />
            <img
              :alt="selectedOffer.product_name + getOfferColor(offer)"
              :src="getMainImage(offer).url"
              class="w-full h-full object-contain"
            />
          </picture>

          <span
            v-else
            class="p-2"
          >
            {{ offer.displayable_offer_properties.find((p) => p.slug === displayProperty.slug)?.value || '' }}
          </span>
        </AppLink>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__offers {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__offers-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__offers-btn {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    min-width: 64px;
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: 12px;
    background-color: $white;
    transition: all 0.3s;

    @apply shadow-sm;

    &:hover {
      border-color: $hell-black-20;
    }

    &--selected {
      border-color: $hell-black-60;
    }
  }
}
</style>
